import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from "@/integrations/supabase/client";
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { Shield, AlertTriangle } from 'lucide-react';
import { useRecaptcha } from '@/hooks/useRecaptcha';
import { useToast } from '@/hooks/use-toast';

const Auth = () => {
  const navigate = useNavigate();
  const { user, signIn } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);

  // Login form state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  
  // reCAPTCHA state
  const { isV3Ready, executeV3, loadV2, renderV2, getV2Response, resetV2 } = useRecaptcha();
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [showV2Captcha, setShowV2Captcha] = useState(false);
  const [v2WidgetId, setV2WidgetId] = useState<number | null>(null);
  const recaptchaContainerRef = useRef<HTMLDivElement>(null);

  // Load failed attempts from sessionStorage
  useEffect(() => {
    const stored = sessionStorage.getItem('login_failed_attempts');
    if (stored) {
      const attempts = parseInt(stored, 10);
      setFailedAttempts(attempts);
      if (attempts >= 2) {
        setShowV2Captcha(true);
      }
    }
  }, []);

  // Load v2 when needed
  useEffect(() => {
    if (showV2Captcha && !v2WidgetId && recaptchaContainerRef.current) {
      loadV2().then(() => {
        renderV2('recaptcha-v2-container').then((widgetId) => {
          setV2WidgetId(widgetId);
        }).catch(error => {
          console.error('Failed to render reCAPTCHA v2:', error);
        });
      });
    }
  }, [showV2Captcha, v2WidgetId, loadV2, renderV2]);

  // Redirect when user is authenticated
  useEffect(() => {
    if (user) {
      navigate('/');
    }
  }, [user, navigate]);


  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      let recaptchaToken: string | null = null;
      let recaptchaVersion: string | null = null;

      // Check which reCAPTCHA version to use
      if (showV2Captcha && v2WidgetId !== null) {
        // Use v2 checkbox
        recaptchaToken = getV2Response(v2WidgetId);
        recaptchaVersion = 'v2';
        
        if (!recaptchaToken) {
          toast({
            title: "Verification Required",
            description: "Please complete the reCAPTCHA verification.",
            variant: "destructive",
          });
          setLoading(false);
          return;
        }
      } else if (isV3Ready) {
        // Use v3 invisible
        try {
          recaptchaToken = await executeV3('login');
          recaptchaVersion = 'v3';
        } catch (error) {
          console.error('reCAPTCHA v3 execution failed:', error);
          // Continue without reCAPTCHA if it fails
        }
      }

      const { error } = await signIn(
        loginEmail, 
        loginPassword,
        recaptchaToken || undefined,
        recaptchaVersion || undefined
      );
      
      if (error) {
        // Increment failed attempts
        const newAttempts = failedAttempts + 1;
        setFailedAttempts(newAttempts);
        sessionStorage.setItem('login_failed_attempts', newAttempts.toString());
        
        // Show v2 after 2 failed attempts
        if (newAttempts >= 2 && !showV2Captcha) {
          setShowV2Captcha(true);
          toast({
            title: "Additional Verification Required",
            description: "Please complete the security check below.",
            variant: "default",
          });
        }
        
        // Reset v2 captcha if shown
        if (v2WidgetId !== null) {
          resetV2(v2WidgetId);
        }
        
        setLoading(false);
        return;
      }
      
      // Clear failed attempts on success
      sessionStorage.removeItem('login_failed_attempts');
      setFailedAttempts(0);
      
      // Navigate immediately after successful login
      navigate('/');
    } catch (error) {
      console.error('Login error:', error);
      setLoading(false);
    }
  };


  return (
    <>
      <SEO 
        title="Staff Login - 4FOX Security"
        description="Staff access portal for authorised personnel"
      />
      <div className="min-h-screen flex flex-col">
        <Navbar />
        
        <main className="flex-1 pt-24 pb-16 gradient-primary">
          <div className="container mx-auto px-4 max-w-md">
            <div className="flex justify-center mb-8">
              <div className="bg-accent/10 p-4 rounded-full">
                <Shield className="w-12 h-12 text-accent" />
              </div>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Staff Access</CardTitle>
                <CardDescription>
                  Enter your credentials to access the admin portal
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email">Email</Label>
                    <Input
                      id="login-email"
                      type="email"
                      placeholder="you@example.com"
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="login-password">Password</Label>
                    <Input
                      id="login-password"
                      type="password"
                      placeholder="••••••••"
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                      required
                    />
                  </div>

                  {showV2Captcha && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                        <AlertTriangle className="w-4 h-4" />
                        <span>Additional security verification required</span>
                      </div>
                      <div 
                        ref={recaptchaContainerRef}
                        id="recaptcha-v2-container" 
                        className="flex justify-center"
                      />
                    </div>
                  )}

                  <Button 
                    type="submit" 
                    className="w-full"
                    disabled={loading}
                  >
                    {loading ? 'Logging in...' : 'Login'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default Auth;
